/**
 * Region Collection
 * Aggregates all region data and provides utility functions
 */

import { Region, RegionCode, RegionFilter } from "../../types/regions";
import { IRAN_REGION } from "./iran";
import { IRAQ_REGION } from "./iraq";
import { SYRIA_REGION } from "./syria";
import { KURDISTAN_REGION } from "./kurdistan";
import { BALUCHISTAN_REGION } from "./baluchistan";
import { AFGHANISTAN_REGION } from "./afghanistan";

/**
 * All available regions
 */
export const ALL_REGIONS: Region[] = [
  IRAN_REGION,
  IRAQ_REGION,
  SYRIA_REGION,
  KURDISTAN_REGION,
  BALUCHISTAN_REGION,
  AFGHANISTAN_REGION,
].sort((a, b) => (a.order ?? 999) - (b.order ?? 999));

/**
 * Get a region by its code
 */
export const getRegionByCode = (code: RegionCode): Region | undefined => {
  return ALL_REGIONS.find((region) => region.code === code);
};

/**
 * Get a region by its ID
 */
export const getRegionById = (id: string): Region | undefined => {
  return ALL_REGIONS.find((region) => region.id === id);
};

/**
 * Get all active regions (status === 'active')
 */
export const getActiveRegions = (): Region[] => {
  return ALL_REGIONS.filter((region) => region.status === "active");
};

/**
 * Get the default region
 */
export const getDefaultRegion = (): Region => {
  const defaultRegion = ALL_REGIONS.find((region) => region.isDefault);
  return defaultRegion || IRAN_REGION;
};

/**
 * Get regions visible to a specific visibility level
 */
export const getRegionsForVisibility = (
  userVisibility: "public" | "registered" | "premium" | "admin"
): Region[] => {
  const visibilityLevels = ["public", "registered", "premium", "admin"];
  const userLevel = visibilityLevels.indexOf(userVisibility);

  return ALL_REGIONS.filter((region) => {
    const regionLevel = visibilityLevels.indexOf(region.visibility);
    return regionLevel <= userLevel && region.status !== "hidden";
  });
};

/**
 * Filter regions based on criteria
 */
export const filterRegions = (filter: RegionFilter): Region[] => {
  let filtered = [...ALL_REGIONS];

  // Filter by status
  if (filter.status) {
    const statuses = Array.isArray(filter.status) ? filter.status : [filter.status];
    filtered = filtered.filter((region) => statuses.includes(region.status));
  }

  // Filter by visibility
  if (filter.visibility) {
    const visibilities = Array.isArray(filter.visibility)
      ? filter.visibility
      : [filter.visibility];
    filtered = filtered.filter((region) => visibilities.includes(region.visibility));
  }

  // Filter by historical data availability
  if (filter.hasHistoricalData !== undefined) {
    filtered = filtered.filter(
      (region) => region.features.hasHistoricalData === filter.hasHistoricalData
    );
  }

  // Filter by search term
  if (filter.search) {
    const searchLower = filter.search.toLowerCase();
    filtered = filtered.filter(
      (region) =>
        region.name.toLowerCase().includes(searchLower) ||
        region.description?.toLowerCase().includes(searchLower) ||
        Object.values(region.localizedNames).some((name) =>
          name?.toLowerCase().includes(searchLower)
        ) ||
        region.tags?.some((tag) => tag.toLowerCase().includes(searchLower))
    );
  }

  // Filter by tags
  if (filter.tags && filter.tags.length > 0) {
    filtered = filtered.filter(
      (region) =>
        region.tags && filter.tags!.some((tag) => region.tags!.includes(tag))
    );
  }

  // Filter by parent region
  if (filter.parentRegion) {
    filtered = filtered.filter(
      (region) =>
        region.parentRegions && region.parentRegions.includes(filter.parentRegion!)
    );
  }

  return filtered;
};

/**
 * Get all unique tags from all regions
 */
export const getAllRegionTags = (): string[] => {
  const tagSet = new Set<string>();
  ALL_REGIONS.forEach((region) => {
    region.tags?.forEach((tag) => tagSet.add(tag));
  });
  return Array.from(tagSet).sort();
};

/**
 * Get regions count by status
 */
export const getRegionCountsByStatus = (): Record<string, number> => {
  const counts: Record<string, number> = {
    active: 0,
    coming_soon: 0,
    hidden: 0,
    deprecated: 0,
  };

  ALL_REGIONS.forEach((region) => {
    counts[region.status] = (counts[region.status] || 0) + 1;
  });

  return counts;
};

/**
 * Get region localized name for current language
 */
export const getRegionLocalizedName = (
  region: Region,
  language: string
): string => {
  return region.localizedNames[language] || region.localizedNames.en || region.name;
};



